import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { adminAPI } from '../../services/api';

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientProjects, setClientProjects] = useState([]);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    phone: ''
  });

  useEffect(() => {
    loadClients();
    loadProjects();
  }, []);

  const loadClients = async () => {
    try {
      const response = await adminAPI.getClients();
      setClients(response.data.clients);
    } catch (error) {
      alert('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await adminAPI.getProjects();
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const loadClientProjects = async (clientId) => {
    try {
      const response = await adminAPI.getClient(clientId);
      // Extract project IDs from the projects array returned by the API
      const projectIds = (response.data.projects || []).map(p => p.id);
      setClientProjects(projectIds);
    } catch (error) {
      alert('Failed to load client projects');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createClient(formData);
      alert('Client created!');
      setShowModal(false);
      setFormData({ email: '', password: '', name: '', company: '', phone: '' });
      loadClients();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create client');
    }
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      email: client.email,
      password: '',
      name: client.name,
      company: client.company || '',
      phone: client.phone || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: formData.name,
        company: formData.company,
        phone: formData.phone
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      await adminAPI.updateClient(selectedClient.id, updateData);
      alert('Client updated!');
      setShowEditModal(false);
      setSelectedClient(null);
      setFormData({ email: '', password: '', name: '', company: '', phone: '' });
      loadClients();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update client');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete client ${name}?`)) return;
    
    try {
      await adminAPI.deleteClient(id);
      alert('Client deleted!');
      loadClients();
    } catch (error) {
      alert('Failed to delete client');
    }
  };

  const handleNotify = async (client) => {
    if (!client || !client.api_key) {
      alert('No notification URL configured for this client');
      return;
    }

    const payload = {
      title: 'App Update',
      body: 'You have an update for your app. Please visit the below link to update your app. Thank You.',
      data: { url: 'https://nextgentechies.sbs/client/login' }
    };

    try {
      await axios.post(client.api_key, payload, { headers: { 'Content-Type': 'application/json' } });
      alert('Notification sent');
    } catch (error) {
      console.error('Notify error:', error);
      alert('Failed to send notification');
    }
  };

  const handleManageAccess = async (client) => {
    setSelectedClient(client);
    try {
      const response = await adminAPI.getClient(client.id);
      // Extract project IDs from the projects array returned by the API
      const projectIds = (response.data.projects || []).map(p => p.id);
      setClientProjects(projectIds);
      setShowAccessModal(true);
    } catch (error) {
      alert('Failed to load client projects');
    }
  };

  const handleToggleProjectAccess = async (projectId) => {
    const hasAccess = clientProjects.includes(projectId);
    
    // Update local state optimistically
    if (hasAccess) {
      setClientProjects(clientProjects.filter(id => id !== projectId));
    } else {
      setClientProjects([...clientProjects, projectId]);
    }
    
    try {
      if (hasAccess) {
        await adminAPI.revokeAccess(selectedClient.id, projectId);
      } else {
        await adminAPI.grantAccess(selectedClient.id, projectId);
      }
      // Reload to ensure backend state is in sync
      await loadClientProjects(selectedClient.id);
    } catch (error) {
      alert('Failed to update project access');
      // Revert optimistic update on error
      await loadClientProjects(selectedClient.id);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0 }}>Clients</h1>
        <button 
          onClick={() => setShowModal(true)}
          style={{
            padding: '12px 24px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          + Add Client
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div className="table-responsive">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Email</th>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Company</th>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Status</th>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(client => (
              <tr key={client.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td data-label="Name" style={{ padding: '16px' }}>{client.name}</td>
                <td data-label="Email" style={{ padding: '16px' }}>{client.email}</td>
                <td data-label="Company" style={{ padding: '16px' }}>{client.company || '-'}</td>
                <td data-label="Status" style={{ padding: '16px' }}>
                  <span style={{
                    padding: '4px 12px',
                    background: client.is_active ? '#d4edda' : '#f8d7da',
                    color: client.is_active ? '#155724' : '#721c24',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    {client.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td data-label="Actions" style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleEdit(client)}
                      style={{
                        padding: '6px 12px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleManageAccess(client)}
                      style={{
                        padding: '6px 12px',
                        background: '#48bb78',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Access
                    </button>
                    <button
                      onClick={() => handleDelete(client.id, client.name)}
                      style={{
                        padding: '6px 12px',
                        background: '#f56565',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => handleNotify(client)}
                      style={{
                        padding: '6px 12px',
                        background: '#3182ce',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Notify
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {clients.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center', color: '#718096' }}>
            No clients yet. Click "Add Client" to create one.
          </div>
        )}
      </div>

      {/* Create Client Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '32px',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ marginTop: 0 }}>Add New Client</h2>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Password *
                </label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#e2e8f0',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Create Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '32px',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ marginTop: 0 }}>Edit Client</h2>
            <form onSubmit={handleUpdate}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Email (cannot be changed)
                </label>
                <input
                  type="email"
                  disabled
                  value={formData.email}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px',
                    background: '#f7fafc',
                    color: '#718096'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  New Password (leave blank to keep current)
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedClient(null);
                    setFormData({ email: '', password: '', name: '', company: '', phone: '' });
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#e2e8f0',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Update Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Project Access Modal */}
      {showAccessModal && selectedClient && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '32px',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '600px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>Manage Project Access</h2>
            <p style={{ marginBottom: '24px', color: '#718096' }}>
              Client: <strong>{selectedClient.name}</strong> ({selectedClient.email})
            </p>
            
            <div style={{ display: 'grid', gap: '12px' }}>
              {projects.map(project => (
                <label
                  key={project.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    background: '#f7fafc',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: clientProjects.includes(project.id) ? '2px solid #667eea' : '2px solid transparent',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={clientProjects.includes(project.id)}
                    onChange={() => handleToggleProjectAccess(project.id)}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '15px' }}>
                      {project.title}
                    </div>
                    {project.description && (
                      <div style={{ fontSize: '13px', color: '#718096', marginTop: '4px' }}>
                        {project.description}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {projects.length === 0 && (
              <div style={{ padding: '24px', textAlign: 'center', color: '#718096' }}>
                No projects available
              </div>
            )}

            <div style={{ marginTop: '24px', textAlign: 'right' }}>
              <button
                onClick={() => {
                  setShowAccessModal(false);
                  setSelectedClient(null);
                  setClientProjects([]);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
